import {
  JobCardPdfAction,
  postV1CompaniesCompanyIdJobCardPdfs,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useState } from 'react';

import FileDownloadResultContext from '~contexts/FileDownloadResultContext';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { RTU_WAITING_TIMEOUT_IN_MS } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { Job } from '../useJob/models';
import { getPdfRenderValidationMessage } from '../useRenderedPdfDownload/models';
import { JobCardPdfRecipientType, toPdfServiceRecipientType } from './models';

interface UseJobCardPdfReturn {
  generateJobCardPdf: (job: Job, recipientType: JobCardPdfRecipientType) => Promise<void>;
  generatingJobIds: Set<string>;
  isGeneratingPdf: (jobId: string) => boolean;
}

export function useJobCardPdf(): UseJobCardPdfReturn {
  const { toasterStore, userStore } = useStores();
  const { enqueueFileDownload } = useContext(FileDownloadResultContext);

  const [generatingJobIds, setGeneratingJobIds] = useState<Set<string>>(new Set());

  const isGeneratingPdf = useCallback(
    (jobId: string) => generatingJobIds.has(jobId),
    [generatingJobIds],
  );

  const generateJobCardPdf = useCallback(
    async (job: Job, recipientType: JobCardPdfRecipientType) => {
      // Cleared via enqueueFileDownload's onSettled below instead of finally — the
      // render is still in flight when the POST resolves.
      let clearGeneratingJobIdOnSettle = true;

      try {
        setGeneratingJobIds((prev) => new Set(prev).add(job.id));

        const companyId = getEffectiveUserCompanyId(userStore);
        let response;

        try {
          response = await postV1CompaniesCompanyIdJobCardPdfs({
            path: { 'company-id': companyId },
            body: {
              job_ids: [job.id],
              recipient_type: toPdfServiceRecipientType(recipientType),
              render_action: JobCardPdfAction.DOWNLOAD,
            },
          });
        } catch (error) {
          const validationMessage = getPdfRenderValidationMessage(error);

          if (validationMessage) {
            toasterStore.push(alert(validationMessage, AlertTypes.info));

            return;
          }

          throw error;
        }

        const renderId = response.data.data.ids[0];

        if (!renderId) {
          toasterStore.push(
            alert(t('dispatch.job_card.generation_failed'), AlertTypes.error),
          );
          return;
        }

        clearGeneratingJobIdOnSettle = false;
        enqueueFileDownload(renderId, {
          onSettled: () => {
            setGeneratingJobIds((prev) => {
              const next = new Set(prev);
              next.delete(job.id);
              return next;
            });
          },
          timeoutMs: RTU_WAITING_TIMEOUT_IN_MS,
        });
      } catch (error) {
        toasterStore.push(
          alert(t('dispatch.job_card.generation_failed'), AlertTypes.error),
        );
        console.error('Failed to generate job card PDF:', error);
      } finally {
        if (clearGeneratingJobIdOnSettle) {
          setGeneratingJobIds((prev) => {
            const next = new Set(prev);
            next.delete(job.id);
            return next;
          });
        }
      }
    },
    [enqueueFileDownload, toasterStore, userStore],
  );

  return {
    generateJobCardPdf,
    generatingJobIds,
    isGeneratingPdf,
  };
}
